import { useState, useRef, useEffect, useCallback } from "react"
import toast from "react-hot-toast"
import { db } from "../services/firebase"
import { collection, addDoc, serverTimestamp } from "firebase/firestore"
import { useUser } from "../contexts/UserContext"

// ── Constants ─────────────────────────────────────────────────────────────────
const DOMAIN = "resolut.co.in"

const PLACEHOLDERS = [
  { label: "Candidate's First Name", value: "{{candidate.firstName}}" },
  { label: "Candidate's Full Name", value: "{{candidate.fullName}}" },
  { label: "Job Role", value: "{{job.role}}" },
  { label: "Your Company Name", value: "{{job.company}}" },
]

// Extracts [Tag] from name — returns tag string or null
const extractTag = (name) => {
  const match = name.match(/^\[([^\]]+)\]/)
  return match ? match[1].trim() : null
}

// ── Placeholder Dropdown ──────────────────────────────────────────────────────
const PlaceholderDropdown = ({ onInsert }) => {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener("mousedown", handler)
    return () => document.removeEventListener("mousedown", handler)
  }, [])

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        type="button"
        onMouseDown={(e) => { e.preventDefault(); setOpen(v => !v) }}
        style={{
          background: "#f4f4f5", border: "1px solid #e4e4e7", borderRadius: 0,
          padding: "6px 10px", fontSize: "11px", fontWeight: 700,
          letterSpacing: "0.07em", textTransform: "uppercase", color: "#18181b",
          cursor: "pointer", fontFamily: "'Satoshi', sans-serif",
          display: "flex", alignItems: "center", gap: "6px",
        }}
      >
        <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M2 4h2M6 4h8M2 8h8M12 8h2M2 12h4M8 12h6" />
        </svg>
        Placeholder
        <svg width="10" height="10" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 6l4 4 4-4" />
        </svg>
      </button>
      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 4px)", left: 0,
          background: "#18181b", border: "1px solid #27272a", borderRadius: 0,
          zIndex: 200, minWidth: "230px", boxShadow: "0 8px 24px rgba(0,0,0,0.22)",
        }}>
          {PLACEHOLDERS.map((p, i) => (
            <button
              key={i}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); onInsert(p.value); setOpen(false) }}
              style={{
                display: "block", width: "100%", textAlign: "left",
                background: "none", border: "none",
                borderBottom: i < PLACEHOLDERS.length - 1 ? "1px solid #27272a" : "none",
                color: "#e4e4e7", fontSize: "11px", fontWeight: 700,
                letterSpacing: "0.08em", textTransform: "uppercase",
                padding: "11px 16px", cursor: "pointer", fontFamily: "'Satoshi', sans-serif",
              }}
              onMouseEnter={e => e.currentTarget.style.background = "#27272a"}
              onMouseLeave={e => e.currentTarget.style.background = "none"}
            >
              {p.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Template Name Input with [Tag] hint ───────────────────────────────────────
const TemplateNameInput = ({ value, onChange }) => {
  const tag = extractTag(value)
  const hasOpenBracket = value.includes("[") && !value.includes("]")

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "6px" }}>
        <label style={{ fontSize: "11px", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#71717a" }}>
          Template Name
        </label>
        <span style={{ fontSize: "11px", color: "#a1a1aa", fontWeight: 500 }}>
          Format: <code style={{ background: "#f4f4f5", padding: "1px 5px", fontSize: "11px", fontFamily: "monospace" }}>[Tag] Name</code>
        </span>
      </div>
      <div style={{ position: "relative" }}>
        <input
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder="e.g. [Reject] On-site Interview"
          style={{
            width: "100%", background: "#fff",
            border: `1px solid ${hasOpenBracket ? "#fbbf24" : tag ? "#18181b" : "#e4e4e7"}`,
            borderRadius: 0, color: "#18181b", fontSize: "14px", fontWeight: 500,
            padding: "10px 12px", paddingRight: tag ? "80px" : "12px",
            outline: "none", boxSizing: "border-box",
            fontFamily: "'Satoshi', sans-serif", transition: "border-color 0.15s",
          }}
          onFocus={e => { if (!hasOpenBracket && !tag) e.target.style.borderColor = "#18181b" }}
          onBlur={e => { if (!hasOpenBracket && !tag) e.target.style.borderColor = "#e4e4e7" }}
        />
        {tag && (
          <span style={{
            position: "absolute", right: "10px", top: "50%", transform: "translateY(-50%)",
            background: "#18181b", color: "#fff", fontSize: "10px", fontWeight: 700,
            letterSpacing: "0.08em", textTransform: "uppercase", padding: "2px 7px",
            pointerEvents: "none",
          }}>
            {tag}
          </span>
        )}
      </div>
      {hasOpenBracket && (
        <p style={{ fontSize: "11px", color: "#d97706", fontWeight: 600, marginTop: "4px", marginBottom: 0 }}>
          Close the bracket to set the tag, e.g. [Confirm]
        </p>
      )}
    </div>
  )
}

// ── Editable From Field ───────────────────────────────────────────────────────
const FromField = ({ value, onChange }) => {
  const handleChange = (e) => {
    // Strip @ and anything after; allow only safe local-part chars
    const raw = e.target.value.replace(/@.*/, "").replace(/[^a-zA-Z0-9._+\-]/g, "")
    onChange(raw)
  }

  return (
    <div>
      <label style={{ display: "block", fontSize: "11px", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#71717a", marginBottom: "6px" }}>
        From
      </label>
      <div
        style={{ display: "flex", alignItems: "center", border: "1px solid #e4e4e7", background: "#fff", transition: "border-color 0.15s" }}
        onFocusCapture={e => e.currentTarget.style.borderColor = "#18181b"}
        onBlurCapture={e => e.currentTarget.style.borderColor = "#e4e4e7"}
      >
        <input
          value={value}
          onChange={handleChange}
          placeholder="noreply"
          style={{
            flex: 1, background: "none", border: "none", outline: "none",
            color: "#18181b", fontSize: "14px", fontWeight: 500,
            padding: "10px 0 10px 12px", fontFamily: "'Satoshi', sans-serif", minWidth: 0,
          }}
        />
        <span style={{
          fontSize: "14px", color: "#a1a1aa", fontWeight: 500,
          padding: "10px 12px 10px 0", whiteSpace: "nowrap", userSelect: "none",
        }}>
          @{DOMAIN}
        </span>
      </div>
    </div>
  )
}

// ── Modal ─────────────────────────────────────────────────────────────────────
const EmailTemplateModal = ({ open, onClose, onSaved, userId }) => {
  const [name, setName] = useState("")
  const [fromLocal, setFromLocal] = useState("")
  const [subject, setSubject] = useState("")
  const [body, setBody] = useState("")
  const [loading, setLoading] = useState(false)

  const { organizationId } = useUser()

  // Track which field was last focused for placeholder routing
  const lastFocusedField = useRef("body") // "subject" | "body"
  const subjectRef = useRef(null)
  const bodyRef = useRef(null)
  const subjectCursor = useRef({ start: 0, end: 0 })
  const bodyCursor = useRef({ start: 0, end: 0 })

  // We need a live ref of subject/body for the insert callback
  const subjectVal = useRef(subject)
  const bodyVal = useRef(body)
  useEffect(() => { subjectVal.current = subject }, [subject])
  useEffect(() => { bodyVal.current = body }, [body])

  useEffect(() => {
    if (open) { setName(""); setFromLocal(""); setSubject(""); setBody("") }
  }, [open])

  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose() }
    if (open) document.addEventListener("keydown", handler)
    return () => document.removeEventListener("keydown", handler)
  }, [open, onClose])

  const trackSubjectCursor = () => {
    const el = subjectRef.current
    if (el) subjectCursor.current = { start: el.selectionStart, end: el.selectionEnd }
  }
  const trackBodyCursor = () => {
    const el = bodyRef.current
    if (el) bodyCursor.current = { start: el.selectionStart, end: el.selectionEnd }
  }

  // Stable insert — reads from refs so no stale closures
  const insertPlaceholder = useCallback((value) => {
    if (lastFocusedField.current === "subject") {
      const { start, end } = subjectCursor.current
      const cur = subjectVal.current
      const newVal = cur.slice(0, start) + value + cur.slice(end)
      setSubject(newVal)
      const newPos = start + value.length
      subjectCursor.current = { start: newPos, end: newPos }
      setTimeout(() => {
        const el = subjectRef.current
        if (el) { el.selectionStart = el.selectionEnd = newPos; el.focus() }
      }, 0)
    } else {
      const { start, end } = bodyCursor.current
      const cur = bodyVal.current
      const newVal = cur.slice(0, start) + value + cur.slice(end)
      setBody(newVal)
      const newPos = start + value.length
      bodyCursor.current = { start: newPos, end: newPos }
      setTimeout(() => {
        const el = bodyRef.current
        if (el) { el.selectionStart = el.selectionEnd = newPos; el.focus() }
      }, 0)
    }
  }, [])

  const handleSave = async () => {
    if (!extractTag(name)) {
      toast.error("Template name must start with a [Tag], e.g. [Confirm] Interview")
      return
    }
    if (!subject.trim()) { toast.error("Subject is required."); return }
    if (!body.trim()) { toast.error("Body is required."); return }

    const from = fromLocal.trim()
      ? `${fromLocal.trim()}@${DOMAIN}`
      : `noreply@${DOMAIN}`

    setLoading(true)
    try {
      await addDoc(collection(db, "emailTemplates"), {
        name: name.trim(),
        from,
        subject: subject.trim(),
        body: body.trim(),
        organizationId: organizationId || null, 
        createdBy: userId || null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      })
      toast.success("Template saved!")
      setTimeout(() => { onSaved?.(); onClose() }, 600)
    } catch (err) {
      console.error(err)
      toast.error("Failed to save template. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  if (!open) return null

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1000,
        background: "rgba(0,0,0,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "'Satoshi', sans-serif",
      }}
    >
      <div
        style={{
          background: "#fff",
          border: "1px solid #e4e4e7",
          borderRadius: 0,
          width: "100%",
          maxWidth: "680px",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 24px 64px rgba(0,0,0,0.14)",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "18px 24px",
            borderBottom: "1px solid #f4f4f5",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexShrink: 0,
          }}
        >
          <div>
            <p
              style={{
                fontSize: "11px",
                fontWeight: 700,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                color: "#a1a1aa",
                marginBottom: "2px",
              }}
            >
              New Template
            </p>
            <h2
              style={{
                fontSize: "17px",
                fontWeight: 700,
                color: "#18181b",
                margin: 0,
              }}
            >
              Create Email Template
            </h2>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "#a1a1aa",
              padding: "4px",
            }}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
            >
              <path d="M2 2l12 12M14 2L2 14" />
            </svg>
          </button>
        </div>

        {/* Scrollable body */}
        <div
          style={{
            padding: "24px",
            overflowY: "auto",
            flex: 1,
            display: "flex",
            flexDirection: "column",
            gap: "16px",
          }}
        >
          <TemplateNameInput value={name} onChange={setName} />
          <FromField value={fromLocal} onChange={setFromLocal} />

          {/* Subject */}
          <div>
            <label
              style={{
                display: "block",
                fontSize: "11px",
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "#71717a",
                marginBottom: "6px",
              }}
            >
              Subject
            </label>
            <input
              ref={subjectRef}
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              onFocus={() => {
                lastFocusedField.current = "subject"
                trackSubjectCursor()
              }}
              onSelect={trackSubjectCursor}
              onKeyUp={trackSubjectCursor}
              onBlur={trackSubjectCursor}
              placeholder="e.g. Confirming your on-site visit, {{candidate.firstName}}!"
              style={{
                width: "100%",
                background: "#fff",
                border: "1px solid #e4e4e7",
                borderRadius: 0,
                color: "#18181b",
                fontSize: "14px",
                fontWeight: 500,
                padding: "10px 12px",
                outline: "none",
                boxSizing: "border-box",
                fontFamily: "'Satoshi', sans-serif",
                transition: "border-color 0.15s",
              }}
              onFocusCapture={(e) => (e.target.style.borderColor = "#18181b")}
              onBlurCapture={(e) => (e.target.style.borderColor = "#e4e4e7")}
            />
          </div>

          {/* Placeholder toolbar */}
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <PlaceholderDropdown onInsert={insertPlaceholder} />
            <span
              style={{ fontSize: "11px", color: "#a1a1aa", fontWeight: 600 }}
            >
              → inserts into{" "}
              <strong
                style={{
                  color: "#71717a",
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  fontSize: "10px",
                }}
              >
                {lastFocusedField.current}
              </strong>
            </span>
          </div>

          {/* Body */}
          <div style={{ flex: 1 }}>
            <label
              style={{
                display: "block",
                fontSize: "11px",
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "#71717a",
                marginBottom: "6px",
              }}
            >
              Body
            </label>
            <textarea
              ref={bodyRef}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              onFocus={() => {
                lastFocusedField.current = "body"
                trackBodyCursor()
              }}
              onSelect={trackBodyCursor}
              onKeyUp={trackBodyCursor}
              onBlur={trackBodyCursor}
              placeholder={`Hi {{candidate.firstName}},\n\nI sent you a calendar invite for your interview date and time. Please confirm that you've received the invite. We're looking forward to your visit!\n\nBest,\n{{Your full name}}`}
              rows={10}
              style={{
                width: "100%",
                background: "#fff",
                border: "1px solid #e4e4e7",
                borderRadius: 0,
                color: "#18181b",
                fontSize: "14px",
                lineHeight: "1.65",
                padding: "12px",
                outline: "none",
                boxSizing: "border-box",
                resize: "vertical",
                fontFamily: "'Satoshi', sans-serif",
                fontWeight: 400,
                transition: "border-color 0.15s",
              }}
              onFocusCapture={(e) => (e.target.style.borderColor = "#18181b")}
              onBlurCapture={(e) => (e.target.style.borderColor = "#e4e4e7")}
            />
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            padding: "16px 24px",
            borderTop: "1px solid #f4f4f5",
            display: "flex",
            justifyContent: "flex-end",
            gap: "10px",
            flexShrink: 0,
          }}
        >
          <button
            type="button"
            onClick={onClose}
            style={{
              background: "#f4f4f5",
              border: "1px solid #e4e4e7",
              borderRadius: 0,
              color: "#71717a",
              fontSize: "11px",
              fontWeight: 700,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              padding: "9px 18px",
              cursor: "pointer",
              fontFamily: "'Satoshi', sans-serif",
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={loading}
            style={{
              background: loading ? "#71717a" : "#18181b",
              border: "none",
              borderRadius: 0,
              color: "#fff",
              fontSize: "11px",
              fontWeight: 700,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              padding: "9px 20px",
              cursor: loading ? "not-allowed" : "pointer",
              fontFamily: "'Satoshi', sans-serif",
            }}
          >
            {loading ? "Saving…" : "Save Template →"}
          </button>
        </div>
      </div>
    </div>
  )
}

export default EmailTemplateModal